import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState, useMemo, useEffect } from "react";
import { AllLoansTable } from "@/components/loans/AllLoansTable";
import { useTopbar } from "@/components/chrome/use-topbar";
import { apiFetch } from "@/lib/api-client";
import type { LoanRow } from "@/components/loans/OpportunitiesTable";
import { MarketFilterDropdown } from "@/components/markets/MarketFilterDropdown";
import { marketIdsQuery } from "@/lib/market-filter";
import { meProfileQueryOptions } from "@/lib/me-profile";
import { TargetFilterDropdown } from "@/components/targets/TargetFilterDropdown";
import type { TargetStatus } from "@/lib/targets-schemas";

type LoansSortKey =
  | "delta"
  | "loan"
  | "arv"
  | "dynamoMax"
  | "borrower"
  | "lender"
  | "property"
  | "status"
  | "date";

interface LoansSearch {
  q: string;
  sort: LoansSortKey;
  order: "asc" | "desc";
  page: number;
}

export const Route = createFileRoute("/_app/loans/")({
  validateSearch: (s: Record<string, unknown>): LoansSearch => ({
    q: typeof s.q === "string" ? s.q : "",
    sort: (typeof s.sort === "string" ? s.sort : "delta") as LoansSortKey,
    order: s.order === "asc" ? "asc" : "desc",
    page: Math.max(Number(s.page ?? 1), 1),
  }),
  component: LoansPage,
});

function LoansPage() {
  const [excludedTargets, setExcludedTargets] = useState<TargetStatus[]>(["won", "lost", "passed"]);
  const { data: profile } = useQuery(meProfileQueryOptions);
  const marketIds = profile?.opportunitiesMarketIds ?? null;

  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const setSearch = (patch: Partial<LoansSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });

  const [searchInput, setSearchInput] = useState(search.q);
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== search.q) setSearch({ q: searchInput, page: 1 });
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const PAGE_SIZE = 50;
  const listUrl =
    `/api/loans?sort=${search.sort}&order=${search.order}&page=${search.page}&pageSize=${PAGE_SIZE}` +
    (search.q ? `&q=${encodeURIComponent(search.q)}` : "") +
    marketIdsQuery(marketIds) +
    (excludedTargets.length ? `&excludeTargetStatus=${excludedTargets.join(",")}` : "");

  const { data } = useQuery<{ rows: LoanRow[]; total: number }>({
    queryKey: ["loans", "all", listUrl],
    queryFn: () => apiFetch<{ rows: LoanRow[]; total: number }>(listUrl),
  });
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const total = data?.total ?? 0;

  // Recover from an out-of-range page: filters that don't reset page (target
  // exclusion, market profile, a shared ?page=N URL) can leave the offset past
  // the end of a smaller result set — the API returns no rows with total > 0.
  useEffect(() => {
    if (data && data.rows.length === 0 && data.total > 0 && search.page > 1) {
      // Replace (not push) so Back doesn't re-enter the bad ?page=N and re-trigger this.
      void navigate({ search: (prev) => ({ ...prev, page: 1 }), replace: true });
    }
  }, [data, search.page]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSort = (key: LoansSortKey) =>
    setSearch(
      key === search.sort
        ? { order: search.order === "asc" ? "desc" : "asc", page: 1 }
        : { sort: key, order: "desc", page: 1 },
    );

  const { setTopbar } = useTopbar();
  useEffect(() => {
    setTopbar({
      crumbs: [{ label: "Operations" }, { label: "Loans" }],
      rightSlot: null,
    });
    return () => setTopbar({ crumbs: [], rightSlot: null });
  }, [setTopbar]);

  return (
    <section className="space-y-6">
      <div className="flex flex-col gap-4 border-b border-rule pb-5 md:flex-row md:items-end md:justify-between md:gap-6">
        <div>
          <h1 className="mb-1 text-3xl font-semibold tracking-tight text-ink">Loans</h1>
          <p className="text-sm text-muted">
            Search all re-underwritten loans and open the deal detail view.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <MarketFilterDropdown />
          <TargetFilterDropdown
            excluded={excludedTargets}
            onChange={(next) => {
              setExcludedTargets(next);
              setSearch({ page: 1 });
            }}
          />
          <button
            type="button"
            disabled
            title="Coming soon"
            className="inline-flex items-center gap-2 rounded-md border border-rule-strong bg-surface px-3.5 py-2 text-xs font-semibold text-ink opacity-60"
          >
            Export loans
          </button>
        </div>
      </div>

      <div>
        <input
          type="text"
          placeholder="Search by borrower, property, city, lender..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-full rounded border border-rule bg-paper px-3 py-2 text-sm placeholder-muted focus:border-ink focus:outline-none"
        />
      </div>

      <AllLoansTable
        rows={rows}
        sortKey={search.sort}
        sortDir={search.order}
        onSort={onSort}
        page={search.page}
        pageSize={PAGE_SIZE}
        total={total}
        onPage={(p) => setSearch({ page: p })}
      />

      <p className="border-l-2 border-tan-dark bg-paper-2 px-5 py-3.5 text-xs leading-relaxed text-muted">
        <strong className="text-ink">Loan detail remains the source of truth.</strong> Open a row to
        inspect ICP scoring, borrower context, and the on-demand PDF preview.
      </p>

      <p className="text-[11px] text-muted-2">
        {total} loan{total !== 1 ? "s" : ""} displayed
      </p>
    </section>
  );
}
